import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import StarIcon from '@mui/icons-material/Star';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import CloseIcon from '@mui/icons-material/Close';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { createCheckoutSession } from '../../services/api';

const PLANS = [
  {
    id: 'download',
    label: 'Pay per Download',
    price: { eur: '€1', usd: '$1.10', gbp: '£0.90' },
    description: 'One-time payment for this resume',
    features: [
      'Download this resume as PDF',
      'Up to 5 downloads per content version',
      'Re-download after edits (counts toward limit)',
    ],
    cta: 'Pay €1 — Download Now',
    badge: null,
  },
  {
    id: 'subscription',
    label: 'Pro — Monthly',
    price: { eur: '€30', usd: '$33', gbp: '£25' },
    description: 'per month · cancel any time',
    features: [
      'Unlimited PDF downloads for all resumes',
      'Unlimited AI resume generations',
      'Priority support',
      'All future features included',
    ],
    cta: 'Subscribe for €30/month',
    badge: 'Best Value',
  },
];

const REASON_COPY = {
  not_paid: {
    title: 'Download Your Resume',
    subtitle: 'Choose a plan to download your AI-generated resume as a professional PDF.',
  },
  download_limit_reached: {
    title: 'Download Limit Reached',
    subtitle:
      'You have used 5 downloads for this content version. Pay again to unlock more downloads, or subscribe for unlimited access.',
  },
  generation_limit: {
    title: 'AI Generation Limit Reached',
    subtitle:
      'You have used all 5 AI generations for this resume. Subscribe to the Pro plan for unlimited generations.',
  },
  default: {
    title: 'Unlock This Feature',
    subtitle: 'Choose a plan to continue.',
  },
  upgrade: {
    title: 'Upgrade to Pro',
    subtitle: 'Unlimited AI generations and PDF downloads across all your resumes.',
  },
};

/**
 * PricingModal
 *
 * Props:
 *   open        — boolean
 *   onClose     — () => void
 *   resumeId    — string | null
 *   reason      — 'not_paid' | 'download_limit_reached' | 'generation_limit'
 *   onSuccess   — (type: 'subscription' | 'download') => void  (called after redirect initiated)
 */
const PricingModal = ({ open, onClose, resumeId, reason = 'not_paid', onSuccess }) => {
  const [selected, setSelected] = useState('download');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const copy = REASON_COPY[reason] || REASON_COPY.default;

  const subscriptionOnly = reason === 'generation_limit' || reason === 'upgrade';
  const visiblePlans = subscriptionOnly
    ? PLANS.filter((p) => p.id === 'subscription')
    : PLANS;

  React.useEffect(() => {
    if (subscriptionOnly) setSelected('subscription');
    else setSelected('download');
  }, [reason, open, subscriptionOnly]);

  const handleContinue = async () => {
    setLoading(true);
    setError('');
    try {
      const { url } = await createCheckoutSession(selected, resumeId);
      if (onSuccess) onSuccess(selected);
      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 0.5, pr: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockOpenIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>{copy.title}</Typography>
        </Box>
        <IconButton
          onClick={onClose}
          disabled={loading}
          size="small"
          sx={{ position: 'absolute', top: 12, right: 12 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          {copy.subtitle}
        </Typography>

        {error && (
          <Alert severity="error" variant="outlined" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {visiblePlans.map((plan) => {
            const isSelected = selected === plan.id;
            return (
              <Box
                key={plan.id}
                onClick={() => !loading && setSelected(plan.id)}
                sx={{
                  position: 'relative',
                  border: 2,
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  p: 2,
                  cursor: loading ? 'default' : 'pointer',
                  bgcolor: isSelected ? 'action.selected' : 'background.paper',
                  transition: 'all 0.15s',
                  '&:hover': !loading ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : {},
                }}
              >
                {plan.badge && (
                  <Chip
                    label={plan.badge}
                    color="success"
                    size="small"
                    sx={{ position: 'absolute', top: -10, right: 12, fontWeight: 700, fontSize: '0.7rem' }}
                  />
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {plan.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {plan.description}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h5" fontWeight={800} color="primary.main" lineHeight={1}>
                      {plan.price.eur}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ~{plan.price.usd} / ~{plan.price.gbp}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 1 }} />

                <List dense disablePadding>
                  {plan.features.map((feature) => (
                    <ListItem key={feature} disablePadding sx={{ py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        {plan.id === 'subscription'
                          ? <AllInclusiveIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          : <CheckCircleIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        }
                      </ListItemIcon>
                      <ListItemText
                        primary={feature}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>

                {isSelected && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CheckCircleIcon color="primary" sx={{ fontSize: 16 }} />
                    <Typography variant="caption" color="primary.main" fontWeight={600}>
                      Selected
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, textAlign: 'center' }}>
          Payments are processed securely by Stripe. No card data touches our servers.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, flexDirection: 'column', gap: 1 }}>
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleContinue}
          disabled={loading}
          startIcon={loading
            ? <CircularProgress size={18} color="inherit" />
            : selected === 'download' ? <DownloadIcon /> : <StarIcon />
          }
          sx={{ borderRadius: 2, fontWeight: 700, py: 1.25 }}
        >
          {loading ? 'Redirecting to Stripe…' : PLANS.find((p) => p.id === selected)?.cta}
        </Button>

        <Button
          fullWidth
          variant="text"
          size="small"
          onClick={onClose}
          disabled={loading}
          sx={{ color: 'text.secondary' }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PricingModal;
